import {
  SAVED_PROPOSED_TACTICS_QUERY_KEY,
  SavedProposedTactics,
  SavedProposedTacticsContext,
} from "@/contexts/saved-proposed-tactics-context";
import { useTeams } from "@/contexts/teams-provider";
import { useApiQuery } from "@/hooks/useApiQuery";
import {
  collectProposedTacticCallIds,
  collectRevisedProposedTacticCallIds,
} from "@/lib/proposed-tactic";
import {
  MAX_PROPOSED_TACTIC_LOOKUPS,
  PromptDTO,
  SavedProposedTacticsDTO,
} from "@shaarp/common";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { UIMessage } from "ai";
import { useCallback, useMemo, type ReactNode } from "react";

/**
 * Which of a thread's tactic proposals were already saved, asked once for the
 * whole thread rather than once per card: a conversation that produced six
 * proposals costs one request on reopening, not six.
 *
 * Keyed by team too, since a tactic handed to one team is not shown from
 * another. The calls of a reply still streaming are asked about as soon as
 * their card appears, so the stream ending changes nothing in the key and the
 * card does not blink. A thread holding more proposals than one lookup may ask
 * about asks about its latest ones (see `MAX_PROPOSED_TACTIC_LOOKUPS`).
 */
export function SavedProposedTacticsProvider({
  messages,
  children,
}: {
  messages: readonly UIMessage[];
  children: ReactNode;
}) {
  const { teamSelected } = useTeams();
  const queryClient = useQueryClient();
  const teamId = teamSelected?.id;

  // Joined into one string so the key only changes when the calls do, not on
  // every chunk that rebuilds the message list.
  const toolCallIds = collectProposedTacticCallIds(messages)
    .slice(-MAX_PROPOSED_TACTIC_LOOKUPS)
    .join(",");
  // Joined for the same reason: a stable set while the thread streams.
  const revisedKey = collectRevisedProposedTacticCallIds(messages).join(",");
  const revised = useMemo(
    () => new Set(revisedKey === "" ? [] : revisedKey.split(",")),
    [revisedKey],
  );

  // The answers already on screen stay while a longer list is asked about
  // (older messages loaded, a new proposal), so no card disappears meanwhile.
  const { data, isFetching } = useApiQuery<SavedProposedTacticsDTO>({
    key: [SAVED_PROPOSED_TACTICS_QUERY_KEY, teamId, toolCallIds],
    path: "/prompts/from-conversation",
    params: { toolCallIds },
    enabled: toolCallIds !== "",
    placeholderData: keepPreviousData,
  });

  // Written into every answer held for this team, so a card remounted after
  // a save or a share shows it, not the form again. A revision's save took
  // the tactic off the calls it replaced, as the API did.
  const markSaved = useCallback(
    (toolCallId: string, prompt: PromptDTO, replaced: readonly string[] = []) => {
      queryClient.setQueriesData<SavedProposedTacticsDTO>(
        { queryKey: [SAVED_PROPOSED_TACTICS_QUERY_KEY, teamId] },
        (current) =>
          current
            ? {
                prompts: {
                  ...current.prompts,
                  ...Object.fromEntries(replaced.map((id) => [id, null])),
                  [toolCallId]: prompt,
                },
              }
            : current,
      );
    },
    [queryClient, teamId],
  );

  const value = useMemo<SavedProposedTactics>(
    () => ({ prompts: data?.prompts ?? {}, isFetching, revised, markSaved }),
    [data, isFetching, revised, markSaved],
  );

  return (
    <SavedProposedTacticsContext.Provider value={value}>
      {children}
    </SavedProposedTacticsContext.Provider>
  );
}
